import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAtom } from 'jotai';
import { includeViewsAtom } from './SearchResult/trpcSearchResultState';

type Props = {
  onSearch: (keyword: string) => void;
  searchKeyword: string;
};

export const SidebarSearchBox: React.FC<Props> = ({ onSearch, searchKeyword }: Props) => {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState(searchKeyword);
  const [includeViews, setIncludeViews] = useAtom(includeViewsAtom);
  const handleSearch = () => {
    if (!keyword) return;
    onSearch(keyword);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
    if (!e.target.value) {
      onSearch('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing || e.key !== 'Enter') return;
    handleSearch();
  };

  const handleSearchOff = useCallback(() => {
    onSearch('');
    setKeyword('');
  }, [onSearch]);

  const handleToggleIncludeViews = useCallback(() => {
    setIncludeViews((prev) => !prev);
  }, [setIncludeViews]);

  // サイドバーのビューモデル検索機能からビューモデルに移動した後に、モデル要素のコピー&ペーストが行えない問題に対処するための処置。
  // 検索テキストボックスのフォーカスが外れているにも関わらず、ペーストイベントの対象が input になるため、モデル要素の貼り付け処理がスキップされていた。
  // 検索ボックスに対してフォーカス状態に連動する data-focus 属性を付与することで、ペーストイベントの対象を判別できるようにする。
  // https://www.notion.so/levii/5f46f11283a64584be7cb5ccb51b8373
  const [focus, onFocus] = useState(false);

  return (
    <div>
      <div className="relative flex">
        <div className="flex min-w-0 flex-1">
          <input
            value={keyword}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => onFocus(true)}
            onBlur={() => onFocus(false)}
            data-focus={focus}
            type="text"
            className="min-w-0 flex-1 rounded-l-md bg-gray-200 py-1 pr-2 placeholder:text-sm focus:outline-none"
            placeholder={t('sidebar.search.placeholder')}
            style={{ paddingInlineStart: '24px' }}
          />
        </div>
        {keyword && (
          <div className="flex w-8 items-center justify-center bg-gray-200 pr-2">
            <button
              className="flex size-4 items-center justify-center rounded-full bg-gray-400 p-3 focus:outline-none"
              onClick={handleSearchOff}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}
        <FontAwesomeIcon
          icon={faSearch}
          className="absolute flex-none text-gray-600"
          style={{ top: '8px', left: '6px' }}
        />
        <button
          className="flex-none rounded-r-md bg-gray-600 px-2 py-1 text-white focus:outline-none"
          onClick={handleSearch}
        >
          {t('common.action.search')}
        </button>
      </div>
      <div className="mt-2 flex items-center">
        <label className="flex cursor-pointer items-center text-sm text-gray-600">
          <input
            type="checkbox"
            checked={includeViews}
            onChange={handleToggleIncludeViews}
            className="form-checkbox mr-2"
          />
          {t('sidebar.search.includeViews')}
        </label>
      </div>
    </div>
  );
};
